import {addToMap, Contract, HttpClient, IConfig, uuidv4generate} from 'Mri.Web.Common';
import {
    CustomerServiceRepresentative,
    MonitoringLog,
    MonitoringStatusItem,
} from '../../../Controllers/CustomerServiceDashboard/CustomerServiceDashboard';
import { UserManagerSettings } from 'oidc-client';
import { AccountStore } from '../../Account/AccountStore';
import Account = Mri.Accounts.Account;
import SearchModel = Mri.Web.Internal.Controllers.Displays.SearchModel;
import PagingResult = Mri.WebApi.PagingResult;
import ActiveDisplayModel = Mri.Web.Internal.Controllers.Displays.ActiveDisplayModel;

const httpClient = new HttpClient();
const initialOidcConfig = (window as any).initialOidcConfig as UserManagerSettings;
const accountStore = new AccountStore(httpClient, initialOidcConfig);
httpClient.setAccessTokenStore(accountStore);

export const getAllMonitoringLogs = async (config: IConfig): Promise<MonitoringLog[]> => {
    return httpClient.get<any>('/api/v1/customer-service-dashboard', config);
};

export const getMonitoringLog = async (logGUID: string, config: IConfig): Promise<MonitoringLog> => {
    return httpClient.get<any>(`/api/v1/customer-service-dashboard/${logGUID}`, config);
};

export const getMonitoringActiveAccounts = async (config: IConfig): Promise<Account[]> => {
    return httpClient.get<any>(`/api/v1/accounts/monitoring-active`, config);
} 

export const getMonitoringLogsByAccountKey = async (
    accountNumber: number,
    config: IConfig
): Promise<MonitoringLog[]> => {
    const { signal } = config;
    return httpClient.get<any>(
        `/api/v1/customer-service-dashboard/get-monitoring-log-by-account/${accountNumber}`,
        config
    );
};

export const loadAccountContracts= async (accountNumber: string, config: IConfig) => {
        return httpClient.get<Mri.Contracts.Contract[]>(
            `/api/v1/accounts/${accountNumber}/contracts`,
            config
        );
}

export const getDisplaysBySearchModel = async (searchModel: SearchModel, config: IConfig): Promise<Mri.WebApi.PagingResult<ActiveDisplayModel>> => {
    return await httpClient.post<Mri.WebApi.PagingResult<ActiveDisplayModel>>('/api/v1/displays/search', searchModel, config) ?? {
        limit: 0,
        offset: 0,
        result: [],
        returned: 0,
        total: 0,
    };
}
export const createMonitoringLogEvent = async (
    monitoringLog: MonitoringLog,
    config: IConfig
): Promise<MonitoringLog> => {
    const { signal } = config;
    return httpClient.put<any>(`/api/v1/customer-service-dashboard`, monitoringLog, config);
};

export const updateMonitoringLogEvent = async (
    monitoringLog: MonitoringLog,
    config: IConfig
): Promise<MonitoringLog> => {
    const { signal } = config;
    return httpClient.post<any>(`/api/v1/customer-service-dashboard`, monitoringLog, config);
};

export const deleteMonitoringLogEvent = async (logGUID: string, config: IConfig): Promise<void> => {
    const { signal } = config;
    return httpClient.delete<any>(`/api/v1/customer-service-dashboard/${logGUID}`, config);
};

export const getAllAccounts = async (config: IConfig): Promise<Mri.Accounts.Account[] | undefined> => {
    const results = httpClient.get<Mri.Accounts.Account[]>('/api/v1/accounts', config);
    return results ?? ([] as Mri.Accounts.Account[]);
};

export const getAccount = async (accountKey: number, config: IConfig): Promise<Mri.Accounts.Account | undefined> => {
    return httpClient.get<Mri.Accounts.Account>(`/api/v1/accounts/${accountKey}`, config)!;
};

export const getAllContracts = async (config: IConfig): Promise<Mri.Contracts.Contract[] | undefined> => {
    const { signal } = config;
    const results = httpClient.get<Mri.Contracts.Contract[]>('/api/v1/contracts', { signal, cache: false });
    return results ?? ([] as Mri.Contracts.Contract[]);
};

export const getAccountContracts = async (
    accountNumber: number,
    config: IConfig
): Promise<Mri.Contracts.Contract[] | undefined> => {
    const { signal } = config;
    const results = httpClient.get<Mri.Contracts.Contract[]>(`/api/v1/accounts/${accountNumber}/contracts`, {
        signal,
        cache: false,
    });
    return results ?? ([] as Mri.Contracts.Contract[]);
};

export interface Assignment {
    UserName: CustomerServiceRepresentative;
    AccountNumber: Account['accountNumber'];
}

export const createAssignment = async (assignment: Assignment, config: IConfig): Promise<void> => {
    return httpClient.put<any>(
        `/api/v1/customer-service-dashboard/create-assignment`,
        assignment as Assignment,
        config
    );
};

export const deleteAssignment = async (assignment: Assignment, config: IConfig): Promise<void> => {
    return httpClient.delete<any>(
        `/api/v1/customer-service-dashboard/delete-user-assignment/${assignment.UserName}/${assignment.AccountNumber}`,
        config
    );
};

export const getMonitoringStatusAccountList = async (config: IConfig): Promise<MonitoringStatusItem[] | undefined> => {
    const { signal } = config;
    const results = httpClient.get<MonitoringStatusItem[]>(
        '/api/v1/customer-service-dashboard/monitoring-status-account-list',
        {
            signal,
            cache: false,
        }
    );
    return results ?? ([] as MonitoringStatusItem[]);
};

export const getCustomerServiceRepresentativesAsync = async (config: IConfig): Promise<string[] | undefined> => {
    const { signal } = config;
    const results = httpClient.get<string[]>(
        `${initialOidcConfig.authority}/api/v1/customer-service-dashboard/get-reps-and-admins`,
        {
            signal,
            cache: false,
        }
    );
    return results ?? ([] as string[]);
};

export const getCustomerServiceAssignmentsAsync = async (
    userName: CustomerServiceRepresentative,
    config: IConfig
): Promise<string[] | undefined> => {
    const { signal } = config;
    const results = httpClient.get<string[]>(`/api/v1/customer-service-dashboard/get-account-assignments/${userName}`, {
        signal,
        cache: false,
    });
    return results ?? ([] as string[]);
};
